import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import each from 'lodash-es/each';
import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import keys from 'lodash-es/keys';
import sortBy from 'lodash-es/sortBy';
import { BehaviorSubject } from 'rxjs';

import { filter, map, takeUntil } from 'rxjs/operators';

import { MediaObserver } from '@ngbracket/ngx-layout';
import { SidenavItem } from '../models/menu-item';
import { SidenavCategory } from "../models/SidenavCategory";
import { componentDestroyed } from '../nitro/utils/component-destroyed';
import { MenuRevolution } from '../models/menu/menu.model';

@Injectable()
export class SidenavService implements OnDestroy {

  mobileBreakpoint = 'lt-md';

  /**
   * Sidenav Items
   * @type {BehaviorSubject<SidenavCategory[]>}
   * @private
   */
  private readonly _items = new BehaviorSubject<SidenavCategory[]>([]);

  items$ = this._items.asObservable();

  get items(): SidenavCategory[] {
    return this._items.getValue();
  }

  set items(items: SidenavCategory[]) {
    this._items.next(items);
  }

  /**
   * Sidenav Items
   * @type {BehaviorSubject<MenuRevolution[]>}
   * @private
   */
  private readonly _itemsRevolution = new BehaviorSubject<MenuRevolution[]>([]);

  itemsRevolution$ = this._itemsRevolution.asObservable();

  get itemsRevolution(): MenuRevolution[] {
    return this._itemsRevolution.getValue();
  }

  set itemsRevolution(itemsRevolution: MenuRevolution[]) {
    this._itemsRevolution.next(itemsRevolution);
  }
  
  private readonly _showID = new BehaviorSubject<boolean>(false);

  showID$ = this._showID.asObservable();

  get showID(): boolean {
    return this._showID.getValue();
  }

  set showID(items: boolean) {
    this._showID.next(items);
  }

  // urls saved

  private readonly _urls= new BehaviorSubject<string[]>([]);

  _urls$ = this._urls.asObservable();

  get urls(): string[] {
    return this._urls.getValue();
  }

  set urls(items: string[]) {
    this._urls.next(items);
  }


  private readonly _allUrlFind = new BehaviorSubject<boolean>(false);

  _allUrlFind$ = this._allUrlFind.asObservable();

  get allUrlFind(): boolean {
    return this._allUrlFind.getValue();
  }

  set allUrlFind(items: boolean) {
    this._allUrlFind.next(items);
  }

  /**
   * Currently Open
   * @type {BehaviorSubject<SidenavCategory[]>}
   * @private
   */
  private readonly _currentlyOpen = new BehaviorSubject<SidenavCategory[]>([]);

  currentlyOpen$ = this._currentlyOpen.asObservable();

  get currentlyOpen(): SidenavCategory[] {
    return this._currentlyOpen.getValue();
  }

  set currentlyOpen(currentlyOpen: SidenavCategory[]) {
    this._currentlyOpen.next(currentlyOpen);
  }

    /**
   * Currently Open
   * @type {BehaviorSubject<SidenavCategory[]>}
   * @private
   */
    private readonly _currentlyOpenRevolution = new BehaviorSubject<MenuRevolution[]>([]);

    currentlyOpenRevolution$ = this._currentlyOpenRevolution.asObservable();
  
    get currentlyOpenRevolution(): MenuRevolution[] {
      return this._currentlyOpenRevolution.getValue();
    }
  
    set currentlyOpenRevolution(currentlyOpen: MenuRevolution[]) {
      this._currentlyOpenRevolution.next(currentlyOpen);
    }

  private readonly _openSubject = new BehaviorSubject<boolean>(this.mediaObserver.isActive(this.mobileBreakpoint));

  open$ = this._openSubject.asObservable();
  private readonly _modeSubject = new BehaviorSubject<'side' | 'over'>(this.mediaObserver.isActive(this.mobileBreakpoint) ? 'over' : 'side');
  mode$ = this._modeSubject.asObservable();
  private readonly _collapsedSubject = new BehaviorSubject<boolean>(false);
  collapsed$ = this._collapsedSubject.asObservable();
  private readonly _expandedSubject = new BehaviorSubject<boolean>(false);
  expanded$ = this._expandedSubject.asObservable();

  constructor(private readonly router: Router,
              private readonly mediaObserver: MediaObserver) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(componentDestroyed(this))
    ).subscribe((event: any) => {
      this.setCurrentlyOpenByRoute(event.url);

      if (this.mediaObserver.isActive(this.mobileBreakpoint)) {
        // Close Sidenav on Mobile after Route Change
        this._openSubject.next(false);
      }
    });

    this.mediaObserver.asObservable().pipe(
      map(() => this.mediaObserver.isActive(this.mobileBreakpoint)),
      takeUntil(componentDestroyed(this))
    ).subscribe(isMobile => {
      if (isMobile) {
        this._openSubject.next(false);
        this._modeSubject.next('over');
        this._collapsedSubject.next(false);
      } else {
        this._openSubject.next(true);
        this._modeSubject.next('side');
      }
    });
  }

  open() {
    this._openSubject.next(true);
  }

  close() {
    this._openSubject.next(false);
  }

  setCollapsed(collapsed: boolean) {
    this._collapsedSubject.next(collapsed);
  }

  toggleCollapsed() {
    this._collapsedSubject.next(!this._collapsedSubject.getValue());
  }

  setExpanded(expanded: boolean) {
    this._expandedSubject.next(expanded);
  }

  toggleExpanded() {
    this._expandedSubject.next(!this._expandedSubject.getValue());
  }

  addItems(items: SidenavCategory[]) {
    items.forEach(item => {
      const i = new SidenavCategory();
      i.jsonModel(item)
      this.addItem(i)
    });
  }

  addCustomReview(items: SidenavCategory[]) {
    let auxRoutes: string [] = [];
    for (let element of items) {
      if (element.items != null && element.items.length != 0) {
        for (let side of element.items) {
          if(side.isItemActive) {
            auxRoutes.push(side.itemRouteOrFunction);
          }
        }
      } else if (element.categoryRouteOrFunction) {
          auxRoutes.push(element.categoryRouteOrFunction);
      }
    }
    this.urls = auxRoutes;
    this.allUrlFind = true;
  }


  addItemsRevolution(items: MenuRevolution[]) {
    items.forEach(item => {
      const i = new MenuRevolution(item);
      this.addItemRevolution(i);
    });
  }

  addCustomReviewRevolution(items: MenuRevolution[]) {
    let auxRoutes: string [] = [];
    for (let element of items) {
      if (element.subMenuItems != null && element.subMenuItems.length != 0) {
        for (let side of element.subMenuItems) {
          if(side.isActive && side.metadata.route) {
            auxRoutes.push(side.metadata.route);
          }
        }
      } else if (element.isActive && element.metadata.pathMatchExact && element.metadata.route) {
          auxRoutes.push(element.metadata.route);
      }
    }
    this.urls = auxRoutes;
    this.allUrlFind = true;
  }

  deleteItems(){
    this.items = [];
  }

  addItem(item: SidenavCategory) {
    const foundIndex = this.items.findIndex((existingItem) => isEqual(existingItem, item));
    if (foundIndex === -1) {
      this.setParentRecursive(item);
      this.items = [...this.items, item];
    }
  }

  deleteItemsRevolution(){
    this.itemsRevolution = [];
  }

  addItemRevolution(item: MenuRevolution) {
    const foundIndex = this.items.findIndex((existingItem) => isEqual(existingItem, item));
    if (foundIndex === -1) {
      this.itemsRevolution = [...this.itemsRevolution, item];
    }
  }

  toggleItemOpen(item: SidenavCategory) {
    let currentlyOpen = this.currentlyOpen;

    if (this.isOpen(item)) {
      if (currentlyOpen.length > 1) {
        currentlyOpen.length = currentlyOpen.indexOf(item);
      } else {
        currentlyOpen = [];
      }
    } else {
      currentlyOpen = this.getParents(item);
    }

    this.currentlyOpen = currentlyOpen;
  }

  toggleItemOpenRevolution(item: MenuRevolution) {
    let currentlyOpen = this.currentlyOpenRevolution;

    if (this.isOpenRevolution(item)) {
      if (currentlyOpen.length > 1) {
        currentlyOpen.length = currentlyOpen.indexOf(item);
      } else {
        currentlyOpen = [];
      }
    } else {
      currentlyOpen = this.getParentsRevolution(item);
    }

    this.currentlyOpenRevolution = currentlyOpen;
  }


  sortRecursive(array: SidenavCategory[], propertyName: string): SidenavCategory[] {
    const that = this;

    array.forEach(function (item: any) {
      const keyArray = keys(item);
      keyArray.forEach(function (key) {
        if (isArray(item[key])) {
          item[key] = that.sortRecursive(item[key], propertyName);
        }
      });
    });

    return sortBy(array, propertyName);
  }

  getItemByRoute(route: any) {
    return this.getItemByRouteRecursive(route, this.items);
  }

  ngOnDestroy(): void { /* TODO document why this method 'ngOnDestroy' is empty */ }

  private getParents(item: SidenavCategory | SidenavItem, items: any [] = []): any {
    items.unshift(item);
    if (item instanceof SidenavItem && item.itemParent) {
      return this.getParents(item.itemParent, items);
    } else {
      return items;
    }
  }

  private getParentsRevolution(item: MenuRevolution, items: any [] = []): any {
    items.unshift(item);
    if (item.parent) {
      return this.getParentsRevolution(item.parent, items);
    } else {
      return items;
    }
  }


  private isOpen(item: SidenavCategory) {
    return (this.currentlyOpen.indexOf(item) > -1);
  }

  private isOpenRevolution(item: MenuRevolution) {
    return (this.currentlyOpenRevolution.indexOf(item) > -1);
  }

  private setCurrentlyOpenByRoute(route: string) {
    const item = this.getItemByRouteRecursive(route, this.items);
    let currentlyOpen = [];
    if (item && item instanceof SidenavItem) {
      currentlyOpen = this.getParents(item);
    } else if (item) {
      currentlyOpen = [item];
    }

    this.currentlyOpen = currentlyOpen;
  }

  private getItemByRouteRecursive(route: string, collection: SidenavCategory[]  ) {
    let result: SidenavCategory | SidenavItem | undefined = collection.find(i => i.categoryRouteOrFunction === route);

    if (!result) {
      each(collection, (item) => {
        if (item?.items && item.items.length > 0) {
          const found = item.items.find(i => i.itemRouteOrFunction === route);
          if (found) {
            result = found;
            return false;
          } else {
            return true;
          }
        } else {
          return true;
        }
      });
    }

    return result;
  }

  private setParentRecursive(item: SidenavCategory) {
    if (item.items && item.items.length > 0) {
      item.items.forEach(subItem => {
        subItem.itemParent= item;
      });
    }
  }

  allowedUrl(): boolean{

    for (let element of this.urls) {
      if (element == this.router.url) {
        return true
      }
    }

    return false;
  }
}
